import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  FormLabel,
  TextField,
  Button as MuiButton,
  Chip,
  Stack,
  IconButton,
  Grid,
  Typography,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import EditIcon from "@mui/icons-material/Edit";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { useForm } from "react-hook-form";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AdditionalFieldsModal } from "./AdditionalFieldsModal";
import {
  convertStringToAdditionalFields,
  convertFormDataToAdditionalFields,
} from "../utils/additionalFieldsHelpers";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";

interface TagFormData {
  tags: string[];
}

interface TagUIState {
  isEditing: boolean;
  displayTags: string[];
}

interface RemarksUIState {
  isEditing: boolean;
  value: string;
}

interface MemoModalProps {
  open: boolean;
  onClose: () => void;
  initialTags?: string[];
  initialRemarks?: string;
  initialAdditionalFields?: string;
  onTagsChange?: (tags: string[]) => void;
  onRemarksChange?: (remarks: string) => void;
  onAdditionalFieldsChange?: (additionalFields: string) => void;
  title?: string;
  availableFields?: AdditionalFieldConfig;
  hasAvailableFields?: boolean;
}

/**
 * メモ（タグ・備考・カスタム項目）を編集するモーダル
 */
export const MemoModal: React.FC<MemoModalProps> = ({
  open,
  onClose,
  initialTags = [],
  initialRemarks = "",
  initialAdditionalFields = "",
  onTagsChange,
  onRemarksChange,
  onAdditionalFieldsChange,
  title = "メモを編集",
  availableFields,
  hasAvailableFields = false,
}) => {
  // タグ管理用のform
  const { control, getValues, reset } = useForm<TagFormData>({
    defaultValues: {
      tags: initialTags,
    },
  });

  // タグUI状態管理
  const [tagUIState, setTagUIState] = useState<TagUIState>({
    isEditing: false,
    displayTags: initialTags,
  });

  // 備考UI状態管理
  const [remarksUIState, setRemarksUIState] = useState<RemarksUIState>({
    isEditing: false,
    value: initialRemarks,
  });

  // 追加項目モーダル状態管理
  const [additionalFieldsModalOpen, setAdditionalFieldsModalOpen] =
    useState(false);

  // 追加項目の現在値を管理（同期用）
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(
    initialAdditionalFields
  );

  // モーダルが開かれたときに初期値をリセット
  useEffect(() => {
    if (open) {
      reset({ tags: initialTags });
      setTagUIState({
        isEditing: false,
        displayTags: initialTags,
      });
      setRemarksUIState({
        isEditing: false,
        value: initialRemarks,
      });
      setCurrentAdditionalFields(initialAdditionalFields);
    }
  }, [open, initialTags, initialRemarks, initialAdditionalFields, reset]);

  const handleTagsComplete = (): void => {
    const currentTags = getValues("tags");
    setTagUIState({
      isEditing: false,
      displayTags: currentTags,
    });
    if (onTagsChange) {
      onTagsChange(currentTags);
    }
  };

  const handleRemarksComplete = (): void => {
    setRemarksUIState((prev) => ({
      ...prev,
      isEditing: false,
    }));
    if (onRemarksChange) {
      onRemarksChange(remarksUIState.value);
    }
  };

  const handleRemarksCancel = (): void => {
    setRemarksUIState({
      isEditing: false,
      value: initialRemarks,
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // 枠外クリックやESCキーでの閉じるを無効化
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onClose();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minHeight: "400px" },
        }}
      >
        <DialogTitle>
          {title}
          <MuiButton
            variant="outlined"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              minWidth: "auto",
              padding: "4px 12px",
              fontSize: "0.875rem",
            }}
          >
            閉じる
          </MuiButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* タグセクション */}
          <Box mb={3}>
            <FormLabel
              sx={{
                fontSize: "0.875rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
              }}
            >
              <LocalOfferIcon fontSize="small" />
              タグ
            </FormLabel>
            <Box ml={0.5}>
              {!tagUIState.isEditing ? (
                <Box>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
                  >
                    {tagUIState.displayTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          height: "24px",
                        }}
                      />
                    ))}
                  </Stack>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setTagUIState((prev) => ({ ...prev, isEditing: true }));
                    }}
                    sx={{
                      padding: "4px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      textTransform: "none",
                    }}
                  >
                    +タグを追加
                  </MuiButton>
                </Box>
              ) : (
                <Box>
                  <CAutocompleteMultiFreeSoloTextField
                    control={control}
                    name="tags"
                    label=""
                    size={SizingWrapperStyle.INHERIT}
                    maxLength={20}
                  />
                  <MuiButton
                    variant="contained"
                    size="small"
                    onClick={handleTagsComplete}
                    sx={{
                      fontSize: "12px",
                      mt: 1,
                      textTransform: "none",
                    }}
                  >
                    入力完了
                  </MuiButton>
                </Box>
              )}
            </Box>
          </Box>

          {/* 備考セクション */}
          <Box mb={3}>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <FormLabel>
                <NoteAltIcon fontSize={"small"} />
                備考
              </FormLabel>
              <IconButton
                size="small"
                onClick={() => {
                  setRemarksUIState((prev) => ({
                    ...prev,
                    isEditing: true,
                  }));
                }}
                sx={{ ml: 1 }}
                aria-label="編集"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            {remarksUIState.isEditing ? (
              <>
                <TextField
                  value={remarksUIState.value}
                  onChange={(e) => {
                    setRemarksUIState((prev) => ({
                      ...prev,
                      value: e.target.value,
                    }));
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  variant="outlined"
                  sx={{ mt: 1, mb: 1 }}
                  inputProps={{ maxLength: 200 }}
                  size="small"
                />
                <Grid
                  container
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Grid item>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={handleRemarksCancel}
                    >
                      キャンセル
                    </MuiButton>
                  </Grid>
                  <Grid item>
                    <MuiButton
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleRemarksComplete}
                    >
                      入力完了
                    </MuiButton>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Typography
                ml={2}
                my={1}
                fontSize={"0.8rem"}
                variant={"body1"}
                component={"div"}
                sx={{
                  whiteSpace: "pre-wrap",
                  minHeight: "1.5rem",
                  wordBreak: "break-word",
                }}
              >
                {remarksUIState.value || ""}
              </Typography>
            )}
          </Box>

          {/* カスタム項目セクション */}
          {hasAvailableFields && (
            <Box>
              <Box display="flex" alignItems="center">
                <FormLabel
                  sx={{
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LibraryAddIcon fontSize="small" />
                  カスタム項目
                </FormLabel>
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    color: "primary.main",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                    "&:hover": {
                      color: "primary.dark",
                    },
                  }}
                  onClick={() => {
                    setAdditionalFieldsModalOpen(true);
                  }}
                >
                  編集
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* カスタム項目編集モーダル */}
      <AdditionalFieldsModal
        open={additionalFieldsModalOpen}
        onClose={() => {
          setAdditionalFieldsModalOpen(false);
        }}
        availableFields={availableFields}
        initialValues={convertStringToAdditionalFields(
          currentAdditionalFields,
          availableFields
        )}
        onSave={(data) => {
          // フラット型からネスト型に変換
          const convertedData = availableFields
            ? convertFormDataToAdditionalFields(data, availableFields)
            : {};
          const stringified = JSON.stringify(convertedData);
          setCurrentAdditionalFields(stringified);
          if (onAdditionalFieldsChange) {
            onAdditionalFieldsChange(stringified);
          }
          setAdditionalFieldsModalOpen(false);
        }}
      />
    </>
  );
};
